import React from 'react';
import { FaCheck } from 'react-icons/fa';

const ARROW_PATTERN = /([\d.]+%|\$?[\d.]+[kMB]?)\s*→\s*([\d.]+%|\$?[\d.]+[kMB]?)/i;

const parseMetricValue = (val) => {
  const num = parseFloat(val.replace(/[$kMB%]/gi, ''));
  if (/k/i.test(val)) return num * 1000;
  if (/M/i.test(val)) return num * 1000000;
  if (/B/i.test(val)) return num * 1000000000;
  return num;
};

const getMetricLabel = (item, beforeStr, afterStr) => {
  const lower = item.toLowerCase();
  if (lower.includes('gross profit margin') || (lower.includes('gross profit') && !lower.includes('net'))) {
    return 'Gross Profit Margin';
  }
  if (lower.includes('net profit margin') || lower.includes('net margin')) return 'Net Profit Margin';
  if (lower.includes('gp') || lower.includes('gross profit')) return 'Gross Profit Margin';
  if (beforeStr.includes('$') || afterStr.includes('$')) return 'Revenue';
  if (beforeStr.includes('%') || afterStr.includes('%')) return 'Gross Profit Margin';
  return 'Result';
};

const calcImprovementPct = (beforeStr, afterStr) => {
  const beforeNum = parseMetricValue(beforeStr);
  const afterNum = parseMetricValue(afterStr);
  if (beforeNum > 0) return Math.round(((afterNum - beforeNum) / beforeNum) * 100);
  if (beforeNum === 0 && afterNum > 0) return null;
  return Math.round(((afterNum - beforeNum) / beforeNum) * 100);
};

// Rough advance widths (em) of bold system-UI glyphs; anything unlisted ≈ a digit.
const GLYPH_EM = { '.': 0.3, ',': 0.3, ' ': 0.28, '%': 0.9, M: 0.86, B: 0.7, k: 0.56, '-': 0.38 };
const textEm = (s) => [...String(s)].reduce((sum, ch) => sum + (GLYPH_EM[ch] ?? 0.6), 0);

// Every value is sized as if it were at least this wide (≈ "$2.43M"), so all
// stories read at one even size; only a longer value shrinks its own pair.
const METRIC_MIN_EM = 3.6;

/**
 * One font size for both halves of a comparison, as large as fits each value on
 * a single line: `cqi` scales it to the box (each box is an inline-size
 * container), capped at the old text-4xl. Browsers without container units drop
 * the declaration and fall back to the text-* classes.
 */
const metricValueStyle = (before, after) => {
  const em = Math.max(METRIC_MIN_EM, textEm(before), textEm(after));
  return { fontSize: `min(2.25rem, ${(96 / em).toFixed(2)}cqi)` };
};

const METRIC_BOX_STYLE = { containerType: 'inline-size' };

export const StoryMetricComparison = ({ label, before, after, improvementPct, isNewFromZero }) => {
  const valueStyle = metricValueStyle(before, after);
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 p-4 sm:p-5">
      {label ? (
        <p className="text-xs font-semibold uppercase tracking-wide text-center text-gray-500 dark:text-white mb-4">
          {label}
        </p>
      ) : null}
      <div className="flex items-stretch justify-center gap-2">
        <div
          className="flex-1 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/50 px-2 py-5 text-center min-w-0"
          style={METRIC_BOX_STYLE}
        >
          <span className="text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">Before</span>
          <p
            className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mt-2 whitespace-nowrap leading-none tracking-tight"
            style={valueStyle}
          >
            {before}
          </p>
        </div>
        <div className="flex items-center shrink-0 text-gray-400 dark:text-gray-500 self-center" aria-hidden>
          <span className="text-2xl font-light leading-none">→</span>
        </div>
        <div
          className="flex-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/50 px-2 py-5 text-center min-w-0"
          style={METRIC_BOX_STYLE}
        >
          <span className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">Now</span>
          <p
            className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mt-2 whitespace-nowrap leading-none tracking-tight"
            style={valueStyle}
          >
            {after}
          </p>
        </div>
      </div>
      {(improvementPct != null || isNewFromZero) && (
        <div className="mt-4 flex justify-center">
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-base font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">
            {isNewFromZero ? 'Built from the ground up' : improvementPct > 0 ? `+${improvementPct}% improvement` : `${improvementPct}%`}
          </span>
        </div>
      )}
    </div>
  );
};

export const StoryBeforeNowPanel = ({ beforeItems, nowItems }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    {beforeItems.length > 0 && (
      <div className="rounded-lg border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300 mb-2">Before BBA</p>
        <ul className="space-y-2">
          {beforeItems.map((text, i) => (
            <li key={i} className="text-sm text-gray-700 dark:text-gray-200 leading-snug flex gap-2">
              <span className="text-red-500 shrink-0 mt-0.5">•</span>
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
    {nowItems.length > 0 && (
      <div className="rounded-lg border-l-4 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-2">Now</p>
        <ul className="space-y-2">
          {nowItems.map((text, i) => (
            <li key={i} className="text-sm text-gray-700 dark:text-gray-200 leading-snug flex gap-2">
              <FaCheck className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5 w-3.5 h-3.5" aria-hidden />
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

export const StoryOutcomeChip = ({ text }) => (
  <div className="flex items-start gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 px-3 py-2.5">
    <FaCheck className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5 w-4 h-4" aria-hidden />
    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{text}</span>
  </div>
);

export const parseStoryResults = (resultItems) => {
  const metrics = [];
  const beforeItems = [];
  const nowItems = [];
  const outcomes = [];

  resultItems.forEach((item, idx) => {
    const arrowMatch = item.match(ARROW_PATTERN);
    if (arrowMatch) {
      const beforeStr = arrowMatch[1];
      const afterStr = arrowMatch[2];
      const beforeNum = parseMetricValue(beforeStr);
      metrics.push({
        key: `metric-${idx}`,
        label: getMetricLabel(item, beforeStr, afterStr),
        before: beforeStr,
        after: afterStr,
        improvementPct: calcImprovementPct(beforeStr, afterStr),
        isNewFromZero: beforeNum === 0 && parseMetricValue(afterStr) > 0
      });
      return;
    }
    if (item.toUpperCase().startsWith('BEFORE:')) {
      beforeItems.push(item.substring(7).trim());
      return;
    }
    if (item.toUpperCase().startsWith('NOW:')) {
      nowItems.push(item.substring(4).trim());
      return;
    }
    const lower = item.toLowerCase();
    if (
      lower.includes('awareness') ||
      lower.includes('increased') ||
      lower.includes('systemized') ||
      lower.includes('modified') ||
      lower.includes('built out') ||
      lower.includes('design team') ||
      lower.includes('full systems') ||
      lower.includes('sops')
    ) {
      outcomes.push(item);
      return;
    }
    outcomes.push(item);
  });

  return { metrics, beforeItems, nowItems, outcomes };
};

export const StoryResultsDisplay = ({ result }) => {
  const { metrics, beforeItems, nowItems, outcomes } = parseStoryResults(result);

  return (
    <div className="space-y-4 mt-2">
      {metrics.map((m) => (
        <StoryMetricComparison
          key={m.key}
          label={m.label}
          before={m.before}
          after={m.after}
          improvementPct={m.improvementPct}
          isNewFromZero={m.isNewFromZero}
        />
      ))}
      {(beforeItems.length > 0 || nowItems.length > 0) && (
        <StoryBeforeNowPanel beforeItems={beforeItems} nowItems={nowItems} />
      )}
      {outcomes.length > 0 && (
        <div className="space-y-2">
          {outcomes.map((text, i) => (
            <StoryOutcomeChip key={`outcome-${i}`} text={text} />
          ))}
        </div>
      )}
    </div>
  );
};
